// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — InsightHubPage (The Oracle)
// Two-mode engine: Real Claude AI or Mock AI (LLM-style narratives)
// Category filter: All / Financial / Operational / Marketing
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Lightbulb, CheckCircle2, Sparkles, Loader, Filter, Brain } from 'lucide-react';
import { useTheme } from '@contexts/ThemeContext';
import { useLang } from '@contexts/LangContext';
import { supabase } from '@lib/supabase';
import { SeldorOracle } from '@lib/ai-engine';
import { PageHeader } from '@components/ui/PageHeader';
import { Button, Badge } from '@components/ui/Primitives';
import { EmptyState } from '@components/ui/States';
import { ActionCard } from '@components/insight-hub/ActionCard';

// Maps insight_type values to the 3 top-level categories
const CATEGORY_MAP = {
  financial: ['financial', 'waste', 'pricing', 'inventory'],
  operational: ['operational', 'fraud', 'staffing'],
  marketing: ['marketing', 'opportunity', 'customer_pattern']
};

export function InsightHubPage({ data, refresh }) {
  const { theme } = useTheme();
  const { t, lang } = useLang();

  const [liveInsights, setLiveInsights] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [lastGenerated, setLastGenerated] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  const engineMode = SeldorOracle.getEngineMode();

  // Identify locally-generated insights by their id prefix so we can remove them
  // from state without a DB call (DB rows get proper dismiss/resolve).
  const isLocalInsight = (id) => {
    const s = String(id || '');
    return s.startsWith('mockai_') || s.startsWith('ai_claude_') || s.startsWith('ai_');
  };

  // Initial load: merge DB insights with a fresh Mock AI run to showcase engine.
  useEffect(() => {
    const dbInsights = data?.insights || [];
    if (dbInsights.length < 3 && data?.sales?.length > 0) {
      (async () => {
        try {
          const fresh = await SeldorOracle.generateInsights(data);
          setLiveInsights([...fresh, ...dbInsights]);
          setLastGenerated(new Date());
        } catch {
          setLiveInsights(dbInsights);
        }
      })();
    } else {
      setLiveInsights(dbInsights);
    }
  }, [data?.insights, data?.sales]);

  const regenerate = useCallback(async () => {
    setGenerating(true);
    try {
      const fresh = await SeldorOracle.generateInsights(data);
      // Keep DB-persisted insights, replace all local (fresh-generated) ones
      const persisted = (data?.insights || []).filter(i => !isLocalInsight(i.id));
      setLiveInsights([...fresh, ...persisted]);
      setLastGenerated(new Date());
    } catch (err) {
      console.error('[InsightHub] Regeneration failed:', err);
    } finally {
      setGenerating(false);
    }
  }, [data]);

  const handleDismiss = useCallback(async (id) => {
    if (isLocalInsight(id)) {
      setLiveInsights(prev => prev.filter(i => i.id !== id));
      return;
    }
    const { error } = await supabase
      .from('insights')
      .update({ is_dismissed: true, dismissed_at: new Date().toISOString() })
      .eq('id', id);
    if (!error) {
      setLiveInsights(prev => prev.filter(i => i.id !== id));
      refresh?.();
    }
  }, [refresh]);

  const handleResolve = useCallback(async (id) => {
    if (isLocalInsight(id)) {
      setLiveInsights(prev => prev.filter(i => i.id !== id));
      return;
    }
    const { error } = await supabase
      .from('insights')
      .update({
        is_resolved: true,
        is_dismissed: true,
        resolved_at: new Date().toISOString()
      })
      .eq('id', id);
    if (!error) {
      setLiveInsights(prev => prev.filter(i => i.id !== id));
      refresh?.();
    }
  }, [refresh]);

  // Counts per category
  const categoryCounts = useMemo(() => {
    const counts = { all: liveInsights.length, financial: 0, operational: 0, marketing: 0 };
    liveInsights.forEach(i => {
      for (const [cat, types] of Object.entries(CATEGORY_MAP)) {
        if (types.includes(i.insight_type)) counts[cat]++;
      }
    });
    return counts;
  }, [liveInsights]);

  const filteredInsights = useMemo(() => {
    if (activeCategory === 'all') return liveInsights;
    const allowed = CATEGORY_MAP[activeCategory] || [];
    return liveInsights.filter(i => allowed.includes(i.insight_type));
  }, [liveInsights, activeCategory]);

  // Engine mode badge
  const modeBadge = engineMode === 'claude'
    ? { label: lang === 'ar' ? 'Claude AI حقيقي' : 'Real Claude AI', variant: 'primary', icon: Sparkles }
    : { label: lang === 'ar' ? 'تحليل استراتيجي محاكى' : 'Simulated Strategic AI', variant: 'info', icon: Brain };
  const ModeIcon = modeBadge.icon;

  return (
    <div>
      <PageHeader
        icon={<Lightbulb size={22} color={theme.primary} />}
        title={t.insightHubTitle}
        description={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Badge variant={modeBadge.variant}>
              <ModeIcon size={10} /> {modeBadge.label}
            </Badge>
            {lastGenerated && (
              <span style={{ fontSize: 11, color: theme.textTertiary }}>
                {lang === 'ar' ? 'آخر تحليل:' : 'Last:'} {lastGenerated.toLocaleTimeString(lang === 'ar' ? 'ar-SA' : 'en-US')}
              </span>
            )}
          </span>
        }
        actions={
          <Button variant="outline" onClick={regenerate} disabled={generating}>
            {generating
              ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> {t.loading}</>
              : <><Sparkles size={14} /> {t.newAnalysis}</>
            }
          </Button>
        }
      />

      {/* Category filters */}
      <div style={{
        display: 'flex',
        gap: 8,
        marginBottom: 20,
        overflowX: 'auto',
        paddingBottom: 4
      }}>
        {[
          { key: 'all', label: lang === 'ar' ? 'الكل' : 'All', count: categoryCounts.all },
          { key: 'financial', label: lang === 'ar' ? 'مالي' : 'Financial', count: categoryCounts.financial },
          { key: 'operational', label: lang === 'ar' ? 'تشغيلي' : 'Operational', count: categoryCounts.operational },
          { key: 'marketing', label: lang === 'ar' ? 'تسويقي' : 'Marketing', count: categoryCounts.marketing }
        ].map(cat => {
          const isActive = activeCategory === cat.key;
          return (
            <div
              key={cat.key}
              onClick={() => setActiveCategory(cat.key)}
              style={{
                padding: '8px 14px',
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
                background: isActive ? theme.primaryDim : theme.bgInput,
                color: isActive ? theme.primary : theme.textSecondary,
                border: `1px solid ${isActive ? theme.primaryBorder : theme.border}`,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                whiteSpace: 'nowrap',
                transition: 'all 0.15s'
              }}
            >
              <Filter size={12} />
              {cat.label}
              {cat.count > 0 && (
                <span style={{
                  background: isActive ? theme.primary : theme.textTertiary,
                  color: '#fff',
                  borderRadius: 10,
                  padding: '1px 7px',
                  fontSize: 10,
                  fontWeight: 700,
                  minWidth: 18,
                  textAlign: 'center'
                }}>
                  {cat.count}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {filteredInsights.length === 0 ? (
        <EmptyState
          icon={<CheckCircle2 size={36} color={theme.success} />}
          title={t.noInsights}
          description={t.noInsightsDesc}
        />
      ) : (
        <div>
          {filteredInsights.map(insight => (
            <ActionCard
              key={insight.id}
              insight={insight}
              onDismiss={handleDismiss}
              onResolve={handleResolve}
            />
          ))}
        </div>
      )}
    </div>
  );
}
